import React from "react";
import { Download, Hourglass, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { plural } from "@/helpers/CommonHelpers";
import { ConflictRulePreview, ConflictRuleProgress } from "@/models/ConflictRule";
import RuleDiagnosticList from "./RuleDiagnosticList";
import RuleOutcomeSummary from "./RuleOutcomeSummary";

interface RulePreviewPanelProps {
  /** Set while a preview streams, with what it has evaluated so far. */
  progress: ConflictRuleProgress | null;
  previewing: boolean;
  preview: ConflictRulePreview | null;
  /** The rule has changed since this preview, so it no longer says what the rule in the form does. */
  stale: boolean;
  onPreviewAgain: () => void;
  canPreview: boolean;
  onDownloadCsv: () => void;
  downloadingCsv: boolean;
}

/**
 * What a rule would do now: how many groups and files it decides and how, the copies that will wait for their winner,
 * and every group it skips, by the check that stopped it. Each line opens a few example groups as the drawer shows
 * them. A preview writes nothing, and a run checks every group again.
 */
const RulePreviewPanel: React.FC<RulePreviewPanelProps> = ({
  progress,
  previewing,
  preview,
  stale,
  onPreviewAgain,
  canPreview,
  onDownloadCsv,
  downloadingCsv,
}) => {
  if (previewing) {
    const percent = progress && progress.parentCount > 0 ? (progress.parentsDone / progress.parentCount) * 100 : 0;
    return (
      <div className="flex flex-col gap-2 rounded-md border border-primary/40 bg-primary/5 p-4">
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <span className="font-medium">Previewing…</span>
          <span className="ml-auto font-mono text-xs text-muted-foreground">{Math.round(percent)}%</span>
        </div>
        <Progress value={percent} className="h-2" />
        {progress && (
          <span className="text-xs text-muted-foreground">
            {progress.parentsDone.toLocaleString()} of {progress.parentCount.toLocaleString()} entities checked ·{" "}
            {plural(progress.filesDecided, "file")} to decide · {plural(progress.groupsSkipped, "group")} skipped so
            far
          </span>
        )}
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className={cn("flex flex-col gap-4 rounded-md border bg-card p-4", stale && "opacity-60")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold">Preview</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onPreviewAgain}
                disabled={!canPreview}
                aria-label="Preview again"
              >
                <RefreshCw className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Preview again</TooltipContent>
          </Tooltip>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={onDownloadCsv}
          disabled={downloadingCsv || stale}
        >
          {downloadingCsv ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          Download preview CSV
        </Button>
      </div>

      {stale && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
          The rule has changed since this preview. Preview it again to see what it does now.
        </p>
      )}

      {preview.warnings.length > 0 && (
        <RuleDiagnosticList
          diagnostics={preview.warnings.map((warning) => ({ ...warning, severity: "warning" as const }))}
        />
      )}

      {preview.copiesThatWait > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Hourglass className="mt-0.5 size-3.5 shrink-0" />
          Waiting copies are held until their group's winner is in SharePoint. Then a renamed copy uploads under a new
          name, and a copy that accepts the SharePoint copy takes over the winner's file.
        </p>
      )}

      <RuleOutcomeSummary
        tense="preview"
        groupsDecided={preview.groupsToDecide}
        filesDecided={preview.filesToDecide}
        copiesThatWait={preview.copiesThatWait}
        groupsSkipped={preview.groupsSkipped}
        actions={preview.actions}
        skipped={preview.skipped}
      />
    </div>
  );
};

export default RulePreviewPanel;
